import { useMutation, useQuery } from '@tanstack/react-query';
import {
  type AttendanceCodeFilter,
  type QueryAdditionalOptions,
  type SaveAttendanceCodeInput,
  type UseQueryReturnType,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import { toast } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { attendanceKeys } from './keys';

const attendanceCodes = graphql(/* GraphQL */ `
  query attendance_attendanceCodes($filter: AttendanceCodeFilter) {
    attendance_attendanceCodes(filter: $filter) {
      id
      name
      description
      code
      active
      visibleForTeacher
      visibleForContact
      nameTextId
      codeType
      sessionCodeType
      custom
    }
  }
`);

const createAttendanceCodes = graphql(/* GraphQL */ `
  mutation attendance_saveAttendanceCode($input: [SaveAttendanceCodeInput]) {
    attendance_saveAttendanceCode(input: $input) {
      id
    }
  }
`);

const attendanceCodesQuery = (filter: AttendanceCodeFilter) => ({
  queryKey: attendanceKeys.codes(filter),
  queryFn: () => gqlClient.request(attendanceCodes, { filter }),
});

export function useAttendanceCodes(
  filter: AttendanceCodeFilter,
  additionalOptions?: Omit<QueryAdditionalOptions, 'academicNamespaceId'>,
) {
  return useQuery({
    ...attendanceCodesQuery(filter),
    ...additionalOptions,
    select: ({ attendance_attendanceCodes }) => attendance_attendanceCodes,
  });
}

export function getAttendanceCodes(filter: AttendanceCodeFilter) {
  return queryClient.fetchQuery(attendanceCodesQuery(filter));
}

export function useCreateOrUpdateAttendanceCode() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: async (input: SaveAttendanceCodeInput[]) =>
      gqlClient.request(createAttendanceCodes, { input }),
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
    onSuccess: (_, [code]) => {
      toast.success(
        code?.id
          ? t('common:snackbarMessages.updateSuccess')
          : t('common:snackbarMessages.createSuccess'),
      );
      queryClient.invalidateQueries({ queryKey: attendanceKeys.all });
    },
  });
}

export type ReturnTypeFromUseAttendanceCodes = UseQueryReturnType<
  typeof useAttendanceCodes
>[number];
